import { type _IProcessingOptions, type ShaderCustomProcessingFunction, type _IShaderProcessingContext } from "core/Engines/Processors/shaderProcessingOptions";
import { GetDOMTextContent, IsWindowObjectExist } from "core/Misc/domManagement";
import { type Nullable } from "core/types";
import { ShaderLanguage } from "./shaderLanguage";
import { type WebGLContext, getStateObject } from "core/Engines/thinEngine.functions";
import { ShaderStore } from "core/Engines/shaderStore";
import { type AbstractEngine } from "core/Engines/abstractEngine";
import { type Effect, type IShaderPath } from "./effect";
import { type IPipelineContext } from "core/Engines/IPipelineContext";
import { Logger } from "core/Misc/logger";
import { Finalize, Initialize, Process } from "core/Engines/Processors/shaderProcessor";
import { _LoadFile } from "core/Engines/abstractEngine.functions";
import { type WebGLPipelineContext } from "core/Engines/WebGL/webGLPipelineContext";

/**
 * Options to be used when creating a pipeline
 */
export interface IPipelineGenerationOptions {
    /**
     * The definition of the shader content.
     * Can be either a unified name, name per vertex and frament or the shader code content itself
     */
    shaderNameOrContent: string | IShaderPath;
    /**
     * Unique key to identify the pipeline.
     * Note that though not mandatory, it's recommended to provide a key to be able to use the automated pipeline loading system.
     */
    key?: string;
    /**
     * The list of defines to be used in the shader
     */
    defines?: string[];

    /**
     * If true, the global defines will be added to the defines array
     */
    addGlobalDefines?: boolean;
    /**
     * The shader language.
     * Defaults to the language suiting the platform name (GLSL for WEBGL2, WGSL for WEBGPU)
     */
    shaderLanguage?: ShaderLanguage;

    /**
     * The name of the platform to be used when processing the shader
     * defaults to WEBGL2
     */
    platformName?: string /* "WEBGL2" | "WEBGL1" | "WEBGPU" */;

    /**
     * extend the processing options when running code processing
     */
    extendedProcessingOptions?: Partial<_IProcessingOptions>;

    /**
     * extend the pipeline generation options
     */
    extendedCreatePipelineOptions?: Partial<ICreateAndPreparePipelineContextOptions>;

    /**
     * If true, generating a new pipeline will return when the pipeline is ready to be used
     */
    waitForIsReady?: boolean;

    /**
     * If true, the pipeline will be created synchronously, even if parallel shader compilation is available
     */
    disableParallelCompilation?: boolean;
}

/**
 * @internal
 */
export interface ICreateAndPreparePipelineContextOptions {
    parallelShaderCompile?: { COMPLETION_STATUS_KHR: number };
    shaderProcessingContext: Nullable<_IShaderProcessingContext>;
    existingPipelineContext?: Nullable<IPipelineContext>;
    name?: string;
    rebuildRebind?: (vertexSourceCode: string, fragmentSourceCode: string, onCompiled: (pipelineContext: IPipelineContext) => void, onError: (message: string) => void) => void;
    onRenderingStateCompiled?: (pipelineContext?: IPipelineContext) => void;
    context?: WebGL2RenderingContext | WebGLRenderingContext;
    // preparePipeline options
    createAsRaw?: boolean;
    vertex: string;
    fragment: string;
    defines: Nullable<string>;
    transformFeedbackVaryings: Nullable<string[]>;
    disableParallelCompilation?: boolean;
}

/**
 * Get a cached pipeline context
 * @param name the pipeline name
 * @param context the context to be used when creating the pipeline
 * @returns the cached pipeline context if it exists
 * @internal
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function getCachedPipeline(name: string, context: WebGLContext): IPipelineContext | undefined {
    const stateObject = getStateObject(context);
    return stateObject.cachedPipelines[name];
}

/**
 * @internal
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function resetCachedPipeline(pipeline: IPipelineContext): void {
    const name = pipeline._name;
    const context = (pipeline as WebGLPipelineContext).context;
    if (name && context) {
        const stateObject = getStateObject(context);
        const cachedPipeline = stateObject.cachedPipelines[name];
        cachedPipeline?.dispose();
        delete stateObject.cachedPipelines[name];
    }
}

/** @internal */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function _ProcessShaderCode(
    processorOptions: _IProcessingOptions,
    baseName: any,
    processFinalCode?: Nullable<ShaderCustomProcessingFunction>,
    onFinalCodeReady?: (vertexCode: string, fragmentCode: string) => void,
    shaderLanguage?: ShaderLanguage,
    engine?: AbstractEngine,
    effectContext?: Effect
) {
    let vertexSource: string | HTMLElement | IShaderPath;
    let fragmentSource: string | HTMLElement | IShaderPath;

    // const baseName = this.name;
    const hostDocument = IsWindowObjectExist() ? engine?.getHostDocument() : null;

    if (typeof baseName === "string") {
        vertexSource = baseName;
    } else if (baseName.vertexSource) {
        vertexSource = "source:" + baseName.vertexSource;
    } else if (baseName.vertexElement) {
        vertexSource = hostDocument?.getElementById(baseName.vertexElement) || baseName.vertexElement;
    } else {
        vertexSource = baseName.vertex || baseName;
    }
    if (typeof baseName === "string") {
        fragmentSource = baseName;
    } else if (baseName.fragmentSource) {
        fragmentSource = "source:" + baseName.fragmentSource;
    } else if (baseName.fragmentElement) {
        fragmentSource = hostDocument?.getElementById(baseName.fragmentElement) || baseName.fragmentElement;
    } else {
        fragmentSource = baseName.fragment || baseName;
    }

    // Important:
    //   The vertex shader must be compiled before the fragment shader, as processing the fragment shader may require information from the vertex shader.
    //   Also, they should be processed in the same JS tick because the shader processor is a singleton and stores information about the current processing in its internal state.
    //   Processing the vertex and fragment shaders in different ticks may cause issues as the internal state may be overridden by another shader being processed in between.
    // This means that the shaders must be processed in sequence and synchronously, but the loading of the shader code can be done in parallel
    // as long as we wait for both shaders to be loaded before starting the processing.
    const shaderCodes: [string | undefined, string | undefined] = [undefined, undefined];
    const shadersLoaded = () => {
        if (shaderCodes[0] && shaderCodes[1]) {
            const [vertexCode, fragmentCode] = shaderCodes;
            Initialize(processorOptions);
            Process(
                vertexCode,
                processorOptions,
                (migratedVertexCode, codeBeforeMigration) => {
                    if (effectContext) {
                        effectContext._vertexSourceCodeBeforeMigration = codeBeforeMigration;
                    }
                    if (processFinalCode) {
                        migratedVertexCode = processFinalCode("vertex", migratedVertexCode);
                    }
                    processorOptions.isFragment = true;
                    Process(
                        fragmentCode,
                        processorOptions,
                        (migratedFragmentCode: string, codeBeforeMigration: string) => {
                            if (effectContext) {
                                effectContext._fragmentSourceCodeBeforeMigration = codeBeforeMigration;
                            }
                            if (processFinalCode) {
                                migratedFragmentCode = processFinalCode("fragment", migratedFragmentCode);
                            }
                            const finalShaders = Finalize(migratedVertexCode, migratedFragmentCode, processorOptions);
                            processorOptions = null as any;
                            const finalCode = UseFinalCode(finalShaders.vertexCode, finalShaders.fragmentCode, baseName, shaderLanguage);
                            onFinalCodeReady?.(finalCode.vertexSourceCode, finalCode.fragmentSourceCode);
                        },
                        engine
                    );
                },
                engine
            );
        }
    };
    LoadShader(
        vertexSource,
        "Vertex",
        "",
        (vertexCode) => {
            if (effectContext) {
                effectContext._rawVertexSourceCode = vertexCode;
            }
            shaderCodes[0] = vertexCode;
            shadersLoaded();
        },
        shaderLanguage
    );
    LoadShader(
        fragmentSource,
        "Fragment",
        "Pixel",
        (fragmentCode) => {
            if (effectContext) {
                effectContext._rawFragmentSourceCode = fragmentCode;
            }
            shaderCodes[1] = fragmentCode;
            shadersLoaded();
        },
        shaderLanguage
    );
}

function LoadShader(shader: any, key: string, optionalKey: string, callback: (data: any) => void, shaderLanguage?: ShaderLanguage, _loadFileInjection?: typeof _LoadFile) {
    if (typeof HTMLElement !== "undefined") {
        // DOM element ?
        if (shader instanceof HTMLElement) {
            const shaderCode = GetDOMTextContent(shader);
            callback(shaderCode);
            return;
        }
    }

    // Direct source ?
    if (shader.substring(0, 7) === "source:") {
        callback(shader.substring(7));
        return;
    }

    // Base64 encoded ?
    if (shader.substring(0, 7) === "base64:") {
        const shaderBinary = window.atob(shader.substring(7));
        callback(shaderBinary);
        return;
    }

    const shaderStore = ShaderStore.GetShadersStore(shaderLanguage);

    // Is in local store ?
    if (shaderStore[shader + key + "Shader"]) {
        callback(shaderStore[shader + key + "Shader"]);
        return;
    }

    if (optionalKey && shaderStore[shader + optionalKey + "Shader"]) {
        callback(shaderStore[shader + optionalKey + "Shader"]);
        return;
    }

    let shaderUrl;

    if (shader[0] === "." || shader[0] === "/" || shader.indexOf("http") > -1) {
        shaderUrl = shader;
    } else {
        shaderUrl = ShaderStore.GetShadersRepository(shaderLanguage) + shader;
    }
    _loadFileInjection = _loadFileInjection || _LoadFile;
    if (!_loadFileInjection) {
        // we got to this point and loadFile was not injected - throw an error
        throw new Error("loadFileInjection is not defined");
    }
    // Vertex shader
    _loadFileInjection(shaderUrl + "." + key.toLowerCase() + ".fx", callback);
}

function UseFinalCode(migratedVertexCode: string, migratedFragmentCode: string, baseName: any, shaderLanguage?: ShaderLanguage) {
    if (baseName) {
        const vertex = baseName.vertexElement || baseName.vertex || baseName.spectorName || baseName;
        const fragment = baseName.fragmentElement || baseName.fragment || baseName.spectorName || baseName;

        return {
            vertexSourceCode: (shaderLanguage === ShaderLanguage.WGSL ? "//" : "") + "#define SHADER_NAME vertex:" + vertex + "\n" + migratedVertexCode,
            fragmentSourceCode: (shaderLanguage === ShaderLanguage.WGSL ? "//" : "") + "#define SHADER_NAME fragment:" + fragment + "\n" + migratedFragmentCode,
        };
    } else {
        return {
            vertexSourceCode: migratedVertexCode,
            fragmentSourceCode: migratedFragmentCode,
        };
    }
}

/**
 * Creates and prepares a pipeline context
 * @internal
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const createAndPreparePipelineContext = (
    options: ICreateAndPreparePipelineContextOptions,
    createPipelineContext: typeof AbstractEngine.prototype.createPipelineContext,
    _preparePipelineContext: typeof AbstractEngine.prototype._preparePipelineContextAsync,
    _executeWhenRenderingStateIsCompiled: typeof AbstractEngine.prototype._executeWhenRenderingStateIsCompiled
): IPipelineContext => {
    try {
        const stateObject = options.context ? getStateObject(options.context) : null;
        if (stateObject) {
            // will not remove the reference to parallelShaderPrecompile, but will prevent it from being used in the next shader compilation
            stateObject.disableParallelShaderCompile = options.disableParallelCompilation;
        }
        const pipelineContext: IPipelineContext = options.existingPipelineContext || createPipelineContext(options.shaderProcessingContext);
        pipelineContext._name = options.name;
        if (options.name && stateObject) {
            stateObject.cachedPipelines[options.name] = pipelineContext;
        }

        // Flagged as async as we may need to delay load some processing tools
        // This does not break anything as the execution is waiting for _executeWhenRenderingStateIsCompiled
        _preparePipelineContext(
            pipelineContext,
            options.vertex,
            options.fragment,
            !!options.createAsRaw,
            "",
            "",
            options.rebuildRebind,
            options.defines,
            options.transformFeedbackVaryings,
            "",
            () => {
                _executeWhenRenderingStateIsCompiled(pipelineContext, () => {
                    options.onRenderingStateCompiled?.(pipelineContext);
                });
            }
        );

        return pipelineContext;
    } catch (e) {
        Logger.Error("Error compiling effect");
        throw e;
    }
};
